import { FC, useMemo } from 'react';
import styles from './pagination.module.sass';
import classNames from 'classnames';

export interface PaginationProps {
  /** Current active page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page is changed */
  onPageChange: (page: number) => void;
  /** Number of sibling pages to show around current page. Default: 1 */
  siblingCount?: number;
  /** Additional CSS class */
  className?: string;
}

/**
 * Calculates which page numbers to display in the pagination.
 * Shows prev/next buttons, page numbers with ellipsis for large ranges.
 *
 * @example
 * - siblingCount=1, currentPage=5, totalPages=10
 * - Shows: [< ] [1] [...] [4] [5] [6] [...] [10] [ >]
 */
const getPaginationItems = (
  currentPage: number,
  totalPages: number,
  siblingCount: number = 1
) => {
  const items: (number | string)[] = [];

  items.push(1);

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 2);
  const rightSiblingIndex = Math.min(
    currentPage + siblingCount,
    totalPages - 1
  );

  if (leftSiblingIndex > 2) {
    items.push('...');
  }

  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    items.push(i);
  }

  if (rightSiblingIndex < totalPages - 1) {
    items.push('...');
  }

  if (totalPages > 1) {
    items.push(totalPages);
  }

  return items;
};

const Pagination: FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className,
}) => {
  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages, siblingCount),
    [currentPage, totalPages, siblingCount]
  );

  const handlePrevClick = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  return (
    <nav
      className={classNames(styles.pagination, className)}
      aria-label="Pagination"
    >
      <button
        className={styles.pageBtn}
        onClick={handlePrevClick}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {paginationItems.map((item, index) => {
        if (item === '...') {
          return (
            <span key={`ellipsis-${index}`} className={styles.pageEllipsis}>
              ...
            </span>
          );
        }

        const pageNum = item as number;
        return (
          <button
            key={pageNum}
            className={classNames(styles.pageBtn, {
              [styles.active]: pageNum === currentPage,
            })}
            onClick={() => handlePageClick(pageNum)}
            aria-label={`Go to page ${pageNum}`}
            aria-current={pageNum === currentPage ? 'page' : undefined}
          >
            {pageNum}
          </button>
        );
      })}

      <button
        className={styles.pageBtn}
        onClick={handleNextClick}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </nav>
  );
};

export default Pagination;
